"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  DensityChart,
  Probability,
} from "@/components/tools/stats-distributions/charts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import { normalPdf } from "@/lib/stats/distributions";
import { densityGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  normalRegion,
  type RegionResult,
  sketchBounds,
} from "@/lib/tool-kit/stats-distributions/normal";
import {
  CLT_RULE_OF_THUMB,
  meanStandardError,
  proportionStandardError,
  successFailureCheck,
} from "@/lib/tool-kit/stats-distributions/sampling";

/** English strings. A translated content file overrides these through `ui`. */
export const SAMPLING_DISTRIBUTION_CALCULATOR_STRINGS = {
  statisticLabel: "Sampling distribution of",
  statisticMean: "The sample mean (x̄)",
  statisticProportion: "The sample proportion (p̂)",
  muLabel: "Population mean (μ)",
  sigmaLabel: "Population standard deviation (σ)",
  pLabel: "Population proportion (p)",
  nLabel: "Sample size (n)",
  eventLabel: "Probability it falls",
  eventBelow: "Below a",
  eventAbove: "Above a",
  eventBetween: "Between a and b",
  aLabel: "Value (a)",
  bLabel: "Upper value (b)",
  results: "Results",
  standardError: "Standard error",
  zScore: "z-score",
  zScores: "z-scores",
  workingHeading: "How this was calculated",
  meanShape:
    "x̄ is approximately normal with mean μ = {mu} and standard error σ / √n = {sigma} / √{n} = {se}.",
  proportionShape:
    "p̂ is approximately normal with mean p = {p} and standard error √(p(1 − p) / n) = {se}.",
  zLine: "z = (a − mean) / SE = ({a} − {mean}) / {se} = {z}",
  cltSmall:
    "With n below 30, this answer relies on the population itself being close to normal. If it is skewed, treat the result with caution or use a larger sample.",
  cltOk:
    "With n = {n}, the central limit theorem makes x̄ approximately normal even if the population is not, unless the population is extremely skewed or has heavy outliers.",
  successOk:
    "np = {np} and n(1 − p) = {nq} are both at least 10, so the normal approximation is reasonable.",
  successPoor:
    "np = {np} or n(1 − p) = {nq} is below 10, so the normal approximation is unreliable here. Use the binomial distribution for exact probabilities.",
  sketchLabel:
    "Sampling distribution curve with the area for the answer shaded",
  sketchCaption:
    "Sampling distribution: mean {mean}, standard error {se}. The shaded area is the probability.",
  enterMean:
    "Enter μ, a σ greater than 0, and a whole-number sample size of at least 1.",
  enterProportion:
    "Enter p between 0 and 1 (not 0 or 1) and a whole-number sample size of at least 1.",
  enterBounds: "Enter the value of a — and b for a range.",
} as const;

type Strings = typeof SAMPLING_DISTRIBUTION_CALCULATOR_STRINGS;
type Statistic = "mean" | "proportion";
type Event = "below" | "above" | "between";

interface Form {
  statistic: Statistic;
  mu: string;
  sigma: string;
  p: string;
  n: string;
  event: Event;
  a: string;
  b: string;
  pa: string;
  pb: string;
}

const INITIAL: Form = {
  statistic: "mean",
  mu: "28",
  sigma: "9",
  p: "0.40",
  n: "40",
  event: "above",
  a: "30",
  b: "31",
  pa: "0.35",
  pb: "0.45",
};

interface Setup {
  centre: number;
  se: number;
  n: number;
  a: number;
  b: number | null;
  symbol: string;
}

/** The sampling distribution's centre and SE plus the event bounds, or what is missing. */
function readSetup(form: Form): Setup | keyof Strings {
  const n = parseIntegerInput(form.n);
  const mean = form.statistic === "mean";
  let centre: number | null;
  let se: number;
  if (mean) {
    centre = parseNumberInput(form.mu);
    const sigma = parseNumberInput(form.sigma);
    if (
      centre === null ||
      sigma === null ||
      sigma <= 0 ||
      n === null ||
      n < 1
    ) {
      return "enterMean";
    }
    se = meanStandardError(sigma, n);
  } else {
    centre = parseNumberInput(form.p);
    if (centre === null || centre <= 0 || centre >= 1 || n === null || n < 1) {
      return "enterProportion";
    }
    se = proportionStandardError(centre, n);
  }
  const a = parseNumberInput(mean ? form.a : form.pa);
  const b =
    form.event === "between" ? parseNumberInput(mean ? form.b : form.pb) : null;
  if (a === null || (form.event === "between" && b === null)) {
    return "enterBounds";
  }
  return { centre, se, n, a, b, symbol: mean ? "x̄" : "p̂" };
}

function notationFor(event: Event, symbol: string, low: string, high: string) {
  if (event === "below") {
    return `P(${symbol} < ${low})`;
  }
  if (event === "above") {
    return `P(${symbol} > ${low})`;
  }
  return `P(${low} < ${symbol} < ${high})`;
}

function Assumptions({
  form,
  setup,
  strings,
  fmt,
}: {
  form: Form;
  setup: Setup;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const four = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  if (form.statistic === "mean") {
    return (
      <Notice>
        {setup.n < CLT_RULE_OF_THUMB
          ? strings.cltSmall
          : t(strings.cltOk, { n: fmt(setup.n) })}
      </Notice>
    );
  }
  const check = successFailureCheck(setup.centre, setup.n);
  const values = {
    np: four(check.expectedSuccesses),
    nq: four(check.expectedFailures),
  };
  return (
    <Notice>
      {t(check.ok ? strings.successOk : strings.successPoor, values)}
    </Notice>
  );
}

function Results({
  form,
  setup,
  result,
  strings,
  fmt,
}: {
  form: Form;
  setup: Setup;
  result: RegionResult;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const four = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  const { centre, se, symbol } = setup;
  const bounds = result.shaded.flat().filter(Number.isFinite);
  const low = Math.min(...bounds);
  const high = Math.max(...bounds);
  const two = result.zHigh !== null;
  const notation = notationFor(form.event, symbol, four(low), four(high));
  const geometry = useMemo(() => {
    const markers = two ? [low, high] : [low];
    const [xMin, xMax] = sketchBounds(centre, se, markers);
    return densityGeometry({
      pdf: (x) => normalPdf(x, centre, se),
      xMin,
      xMax,
      shaded: result.shaded,
      markers,
    });
  }, [centre, se, result, two, low, high]);
  const zLine = (value: number) =>
    t(strings.zLine, {
      a: four(value),
      mean: four(centre),
      se: four(se),
      z: four((value - centre) / se),
    });
  const shape =
    form.statistic === "mean"
      ? t(strings.meanShape, {
          mu: four(centre),
          sigma: four(parseNumberInput(form.sigma) ?? 0),
          n: fmt(setup.n),
          se: four(se),
        })
      : t(strings.proportionShape, { p: four(centre), se: four(se) });
  const zText = two
    ? `${four(result.zLow)}, ${four(result.zHigh as number)}`
    : four(result.zLow);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={notation}
          value={<Probability fmt={fmt} value={result.probability} />}
        />
        <StatCard label={strings.standardError} value={four(se)} />
        <StatCard
          label={two ? strings.zScores : strings.zScore}
          value={zText}
        />
      </StatGrid>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="text-muted-foreground text-sm">{shape}</p>
        <p className="font-mono text-muted-foreground text-sm">{zLine(low)}</p>
        {two ? (
          <p className="font-mono text-muted-foreground text-sm">
            {zLine(high)}
          </p>
        ) : null}
      </div>
      <Assumptions fmt={fmt} form={form} setup={setup} strings={strings} />
      <div className={`${CARD} space-y-3`}>
        <DensityChart
          format={(value) => fmt(value, { maximumFractionDigits: 3 })}
          geometry={geometry}
          label={strings.sketchLabel}
        />
        <p className="text-muted-foreground text-sm">
          {t(strings.sketchCaption, { mean: four(centre), se: four(se) })}
        </p>
      </div>
    </>
  );
}

function Inputs({
  form,
  update,
  strings,
}: {
  form: Form;
  update: (patch: Partial<Form>) => void;
  strings: Strings;
}) {
  const mean = form.statistic === "mean";
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        {mean ? (
          <>
            <TextField
              id="sd-mu"
              label={strings.muLabel}
              numeric
              onChange={(mu) => update({ mu })}
              value={form.mu}
            />
            <TextField
              id="sd-sigma"
              label={strings.sigmaLabel}
              numeric
              onChange={(sigma) => update({ sigma })}
              value={form.sigma}
            />
          </>
        ) : (
          <TextField
            id="sd-p"
            label={strings.pLabel}
            numeric
            onChange={(p) => update({ p })}
            value={form.p}
          />
        )}
        <TextField
          id="sd-n"
          label={strings.nLabel}
          numeric
          onChange={(n) => update({ n })}
          value={form.n}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="sd-event"
          label={strings.eventLabel}
          onChange={(event) => update({ event })}
          options={[
            { value: "below", label: strings.eventBelow },
            { value: "above", label: strings.eventAbove },
            { value: "between", label: strings.eventBetween },
          ]}
          value={form.event}
        />
        <TextField
          id="sd-a"
          label={strings.aLabel}
          numeric
          onChange={(value) => update(mean ? { a: value } : { pa: value })}
          value={mean ? form.a : form.pa}
        />
        {form.event === "between" ? (
          <TextField
            id="sd-b"
            label={strings.bLabel}
            numeric
            onChange={(value) => update(mean ? { b: value } : { pb: value })}
            value={mean ? form.b : form.pb}
          />
        ) : null}
      </div>
    </>
  );
}

export default function SamplingDistributionCalculatorWidget() {
  const strings = useToolUi(SAMPLING_DISTRIBUTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [form, setForm] = useState<Form>(INITIAL);
  const update = (patch: Partial<Form>) =>
    setForm((current) => ({ ...current, ...patch }));
  const setup = readSetup(form);
  const result =
    typeof setup === "string"
      ? null
      : normalRegion(form.event, setup.centre, setup.se, setup.a, setup.b);

  return (
    <>
      <section className="space-y-5">
        <PillGroup
          label={strings.statisticLabel}
          name="sd-statistic"
          onChange={(statistic) => update({ statistic })}
          options={[
            { value: "mean", label: strings.statisticMean },
            { value: "proportion", label: strings.statisticProportion },
          ]}
          value={form.statistic}
        />
        <Inputs form={form} strings={strings} update={update} />
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typeof setup === "string" ? <Notice>{strings[setup]}</Notice> : null}
        {typeof setup === "object" && result ? (
          <Results
            fmt={fmt}
            form={form}
            result={result}
            setup={setup}
            strings={strings}
          />
        ) : null}
      </section>
    </>
  );
}
